import {
  parseConfigFileText,
  type ParsedConfigFile
} from "../../spot_shared/src/config/json-config";
import { type RadioStateModel } from "../../spot_shared/src/types/radio-types";
import { sanitizeChannels } from "../../spot_shared/src/radio/channel-normalization";
import {
  SpotRadioCore,
  type RadioConnectionInfo,
  type WriteProgress
} from "../../spot_shared/src/radio/radio-core";
import { WebSerialTransport } from "../../spot_shared/src/transport/web-serial-transport";
import { renderWriterHelpView } from "./views/writer-help-view";
import { renderStatusView } from "./views/status-view";
import { renderWriterFrame } from "./views/writer-frame-view";
import { renderWriterStepsView } from "./views/writer-steps-view";

declare const __APP_VERSION__: string;

type Tone = "neutral" | "good" | "warn" | "err";
type ProgressTone = "neutral" | "success" | "failure";

type ParentConfigMessage = {
  type: "spot-writer-config";
  configText: string;
  configSource?: string;
  showReturnButton?: boolean;
};

type StatusCode =
  | "ready"
  | "config-accepted"
  | "config-rejected"
  | "write-started"
  | "write-succeeded"
  | "write-failed";

type WriterStatusEvent = {
  type: "spot-writer-status";
  code: StatusCode;
  message: string;
};

type LoadedConfigState = {
  fileName: string;
  config: ParsedConfigFile;
  adjustedCount: number;
  activeChannelCount: number;
};

type AppState = {
  busy: boolean;
  loaded: LoadedConfigState | null;
  connection: RadioConnectionInfo | null;
  showHelp: boolean;
  statusTone: Tone;
  statusMessage: string;
  progressCurrent: number;
  progressTotal: number;
  progressTone: ProgressTone;
  compatibilityWarning: string;
  lastError: string;
  writeSupportDetail: string;
  configSource: string;
  showReturnButton: boolean;
};

const secureContextAvailable = typeof window !== "undefined" ? window.isSecureContext : false;
const webSerialSupported = typeof navigator !== "undefined" && "serial" in navigator;
const embeddedMode = new URLSearchParams(window.location.search).has("embedded");
const defaultConfigSource = "your organization";

function getInvalidEmbeddedConfigMessage(configSource: string): string {
  return configSource === defaultConfigSource
    ? "The parent site supplied an invalid SPOT configuration."
    : `${configSource} supplied an invalid SPOT configuration.`;
}

function getInitialStatusMessage(webSerialSupported: boolean): string {
  if (webSerialSupported) {
    return embeddedMode
      ? "Waiting for configuration from the parent site..."
      : "Load a SPOT JSON file to get started.";
  }

  const secureContextHint = secureContextAvailable
    ? ""
    : " Also make sure this page is opened from a secure address (https).";
  return `Your browser cannot communicate with the receiver over USB. Try Chrome, Edge, Firefox, or another modern browser (not Safari).${secureContextHint}`;
}

const initialState: AppState = {
  busy: false,
  loaded: null,
  connection: null,
  showHelp: false,
  statusTone: webSerialSupported ? "neutral" : "warn",
  statusMessage: getInitialStatusMessage(webSerialSupported),
  progressCurrent: 0,
  progressTotal: 0,
  progressTone: "neutral",
  compatibilityWarning: "",
  lastError: "",
  writeSupportDetail: "",
  configSource: defaultConfigSource,
  showReturnButton: false
};

function isPortSelectionCancelled(error: unknown): boolean {
  return error instanceof Error && error.name === "NotFoundError";
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function formatWriteSupportDetail(error: unknown, current: number, total: number): string {
  const detail = error instanceof Error ? error.message : String(error);
  return total > 0 ? `${detail} (${current} of ${total} blocks complete)` : detail;
}

async function forgetRememberedPorts(): Promise<void> {
  if (!("serial" in navigator) || typeof navigator.serial?.getPorts !== "function") {
    return;
  }

  const ports = await navigator.serial.getPorts();
  for (const port of ports) {
    if (typeof port.forget === "function") {
      await port.forget();
    }
  }
}

class SpotWebWriterApp {
  private state: AppState = initialState;
  private readonly transport = new WebSerialTransport();
  private readonly radio = new SpotRadioCore(this.transport);
  // Writer has no user-configurable startup profile, so it always uses the
  // same built-in baseline as SPOT Editor for partial JSON configurations.
  private readonly importDefaultRadio = this.loadImportDefaultRadio();
  private awaitingPostWriteDisconnect = false;

  constructor(private readonly root: HTMLElement) {
    this.radio.setDisconnectHandler((reason) => {
      const expectedResetDisconnect = this.awaitingPostWriteDisconnect;
      this.awaitingPostWriteDisconnect = false;
      this.setState({
        connection: null,
        busy: false,
        compatibilityWarning: "",
        lastError: "",
        progressTone: expectedResetDisconnect ? "success" : this.state.progressTone,
        statusTone: expectedResetDisconnect ? "good" : "warn",
        statusMessage: expectedResetDisconnect
          ? "Receiver restarting"
          : reason
      });
    });
  }

  mount(): void {
    this.render();
    if (embeddedMode) {
      window.addEventListener("message", this.handleParentMessage);
      this.postStatus("ready", "SPOT Writer is ready to receive a configuration.");
    }
  }

  private postStatus(code: StatusCode, message: string): void {
    if (!embeddedMode) {
      return;
    }

    const statusMessage: WriterStatusEvent = { type: "spot-writer-status", code, message };
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(statusMessage, "*");
    }
    if (window.opener) {
      window.opener.postMessage(statusMessage, "*");
    }
  }

  private handleParentMessage = (event: MessageEvent): void => {
    if (!embeddedMode || !this.isParentConfigMessage(event.data)) {
      return;
    }

    const configText = event.data.configText.trim();
    const configSource = event.data.configSource?.trim() || defaultConfigSource;
    const showReturnButton = event.data.showReturnButton === true;

    if (this.state.busy) {
      const message = "The writer is busy and cannot accept a new configuration yet.";
      this.postStatus("config-rejected", message);
      return;
    }

    if (configText.length === 0) {
      const message = "Could not parse the JSON source.";
      this.setState({
        busy: false,
        loaded: null,
        configSource,
        showReturnButton,
        statusTone: "err",
        statusMessage: getInvalidEmbeddedConfigMessage(configSource),
        progressTone: "failure",
        lastError: "The supplied configuration was empty."
      });
      this.postStatus("config-rejected", message);
      return;
    }

    this.setState({
      configSource,
      showReturnButton
    });
    this.beginLoad(`${configSource} configuration`);
    void this.loadConfigText(configText, `${configSource} configuration`, true);
  };

  private isParentConfigMessage(data: unknown): data is ParentConfigMessage {
    if (typeof data !== "object" || data === null) {
      return false;
    }

    const candidate = data as Partial<ParentConfigMessage>;
    return candidate.type === "spot-writer-config" && typeof candidate.configText === "string";
  }

  private setState(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.render();
  }

  private handleReturnToSource(): void {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.close();
  }

  private render(): void {
    const loaded = this.state.loaded
      ? {
          fileName: this.state.loaded.fileName,
          activeChannelCount: this.state.loaded.activeChannelCount,
          totalChannelCount: this.state.loaded.config.channels.length
        }
      : null;

    this.root.innerHTML = renderWriterFrame({
      version: __APP_VERSION__,
      status: renderStatusView({
        tone: this.state.statusTone,
        message: this.state.statusMessage,
        compatibilityWarning: this.state.compatibilityWarning,
        error: this.state.lastError,
        progressCurrent: this.state.progressCurrent,
        progressTotal: this.state.progressTotal,
        progressTone: this.state.progressTone
      }),
      steps: renderWriterStepsView({
        busy: this.state.busy,
        showFileLoad: !embeddedMode,
        loaded,
        injectedConfigSource: this.state.configSource
      }),
      helpCard: renderWriterHelpView(this.state.showHelp, this.state.writeSupportDetail),
      helpExpanded: this.state.showHelp,
      showFileInput: !embeddedMode,
      returnSource: this.state.showReturnButton ? this.state.configSource : null
    });

    this.bindEvents();
  }

  private bindEvents(): void {
    this.root.querySelector<HTMLButtonElement>("#load-btn")?.addEventListener("click", () => {
      this.root.querySelector<HTMLInputElement>("#file-input")?.click();
    });
    this.root.querySelector<HTMLInputElement>("#file-input")?.addEventListener("change", (event) => {
      const input = event.currentTarget as HTMLInputElement | null;
      const file = input?.files?.[0];
      if (file) {
        void this.handleLoadFile(file);
      }
      if (input) {
        input.value = "";
      }
    });
    this.root.querySelector<HTMLButtonElement>("#write-btn")?.addEventListener("click", () => {
      void this.handleWrite();
    });
    this.root.querySelector<HTMLButtonElement>("#hero-help-btn")?.addEventListener("click", () => {
      if (!this.state.showHelp) {
        this.setState({ showHelp: true });
      }
    });
    this.root.querySelector<HTMLButtonElement>("#help-close-btn")?.addEventListener("click", () => {
      if (this.state.showHelp) {
        this.setState({ showHelp: false });
      }
    });
    this.root.querySelector<HTMLButtonElement>("#return-btn")?.addEventListener("click", () => {
      this.handleReturnToSource();
    });
  }

  private async handleLoadFile(file: File): Promise<void> {
    this.beginLoad(file.name);
    try {
      await this.loadConfigText(await file.text(), file.name);
    } catch (error) {
      this.setState({
        busy: false,
        statusTone: "err",
        statusMessage: `Could not load ${file.name}.`,
        progressTone: "failure",
        lastError: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private beginLoad(sourceLabel: string): void {
    this.setState({
      busy: true,
      loaded: null,
      statusTone: "neutral",
      statusMessage: `Loading ${sourceLabel}...`,
      progressCurrent: 0,
      progressTotal: 0,
      progressTone: "neutral",
      lastError: ""
    });
  }

  /** Loads the built-in radio baseline that fills omitted JSON radio settings. */
  private async loadImportDefaultRadio(): Promise<RadioStateModel> {
    const response = await fetch("radio_templates/default-radio.json", { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`Could not load the built-in radio default: HTTP ${response.status}`);
    }
    return parseConfigFileText(await response.text()).radio;
  }

  private async loadConfigText(configText: string, sourceLabel: string, fromParent = false): Promise<void> {
    try {
      const parsed = parseConfigFileText(configText, await this.importDefaultRadio);
      const normalized = sanitizeChannels(parsed.channels);
      const loaded: LoadedConfigState = {
        fileName: sourceLabel,
        config: {
          channels: normalized.channels,
          radio: parsed.radio,
          deviceId: parsed.deviceId
        },
        adjustedCount: normalized.adjustedCount,
        activeChannelCount: normalized.channels.filter((channel) => channel.active).length
      };

      this.setState({
        busy: false,
        loaded,
        statusTone: "good",
        statusMessage:
          normalized.adjustedCount > 0
            ? `Loaded ${loaded.activeChannelCount} active channels (${loaded.config.channels.length} total, ${normalized.adjustedCount} sanitized)`
            : `Loaded ${loaded.activeChannelCount} active channels (${loaded.config.channels.length} total)`,
        lastError: ""
      });
      if (fromParent) {
        this.postStatus("config-accepted", "Configuration accepted.");
      }
    } catch (error) {
      const message = "Could not parse the JSON source.";
      this.setState({
        busy: false,
        statusTone: "err",
        statusMessage: fromParent
          ? getInvalidEmbeddedConfigMessage(this.state.configSource)
          : `Could not load ${sourceLabel}.`,
        progressTone: "failure",
        lastError: error instanceof Error ? error.message : String(error)
      });
      if (fromParent) {
        this.postStatus("config-rejected", message);
      }
    }
  }

  private async ensureConnectedForWrite(): Promise<boolean> {
    if (this.state.connection) {
      return true;
    }

    this.awaitingPostWriteDisconnect = false;
    this.setState({
      busy: true,
      statusTone: "neutral",
      statusMessage: "Connecting to receiver...",
      progressTone: "neutral",
      lastError: ""
    });

    try {
      let connection: RadioConnectionInfo;
      try {
        connection = await this.radio.connect();
      } catch (initialError) {
        if (isPortSelectionCancelled(initialError)) {
          throw initialError;
        }

        // A receiver can expose its serial port before its firmware is ready
        // to answer the first session request after a reset.
        await wait(1500);
        connection = await this.radio.connect();
      }
      this.setState({
        connection,
        compatibilityWarning: connection.compatibilityWarning,
        statusTone: connection.compatibilityWarning ? "warn" : "good",
        statusMessage: connection.compatibilityWarning
          ? "Receiver connected with a compatibility warning."
          : "Receiver connected.",
        lastError: ""
      });
      return true;
    } catch (error) {
      const selectionCancelled = isPortSelectionCancelled(error);
      const message = selectionCancelled ? "Write cancelled." : "Could not connect to the receiver.";
      if (!selectionCancelled) {
        await forgetRememberedPorts().catch(() => {});
      }
      this.setState({
        busy: false,
        connection: null,
        compatibilityWarning: "",
        statusTone: selectionCancelled ? "warn" : "err",
        statusMessage: message,
        progressTone: selectionCancelled ? "neutral" : "failure",
        lastError: selectionCancelled ? "" : error instanceof Error ? error.message : String(error)
      });
      if (!selectionCancelled) {
        this.postStatus("write-failed", message);
      }
      return false;
    }
  }

  private async handleWrite(): Promise<void> {
    if (!this.state.loaded) {
      return;
    }

    // A new attempt supersedes any technical detail from the prior write.
    this.setState({ writeSupportDetail: "" });

    if (!(await this.ensureConnectedForWrite())) {
      return;
    }

    // Embedded integrations own their configuration provenance. Standalone
    // Writer applies the same receiver-ID guard as SPOT Editor before it writes
    // a radio-specific JSON configuration.
    const sourceDeviceId = this.state.loaded.config.deviceId;
    if (!embeddedMode && sourceDeviceId) {
      let identityWarning = "";
      try {
        const targetDeviceId = await this.radio.readDeviceId();
        if (targetDeviceId !== sourceDeviceId) {
          identityWarning = `This file is for receiver ${sourceDeviceId}, but the connected receiver is ${targetDeviceId}.`;
        }
      } catch {
        identityWarning = `This file is for receiver ${sourceDeviceId}, but the connected receiver does not provide a device ID for verification.`;
      }
      if (identityWarning && !window.confirm(`${identityWarning}\n\nWrite anyway?`)) {
        this.setState({
          statusTone: "warn",
          statusMessage: "Write cancelled.",
          progressTone: "neutral",
          lastError: ""
        });
        return;
      }
    }

    this.setState({
      busy: true,
      statusTone: "neutral",
      statusMessage: "Preparing write plan...",
      progressCurrent: 0,
      progressTotal: 0,
      progressTone: "neutral",
      lastError: ""
    });
    this.postStatus("write-started", "Writing has started.");

    try {
      const channelPlan = await this.radio.prepareWritePlan(this.state.loaded.config.channels);
      const settingsPlan = await this.radio.prepareRadioSettingsWritePlan(this.state.loaded.config.radio);
      const totalBlocks = channelPlan.blocks.length + settingsPlan.blocks.length;

      if (totalBlocks === 0) {
        this.setState({
          busy: false,
          statusTone: "good",
          statusMessage: "No receiver changes were needed.",
          progressCurrent: 0,
          progressTotal: 0,
          progressTone: "success"
        });
        this.postStatus("write-succeeded", "No receiver changes were needed.");
        return;
      }

      const updateProgress = (base: number) => (update: WriteProgress) => {
        this.setState({
          progressCurrent: base + update.current,
          progressTotal: totalBlocks,
          progressTone: "success",
          statusTone: update.phase === "verify" ? "neutral" : "good",
          statusMessage: update.message
        });
      };

      if (channelPlan.blocks.length > 0) {
        await this.radio.writeToRadio(this.state.loaded.config.channels, updateProgress(0), true);
      }

      if (settingsPlan.blocks.length > 0) {
        await this.radio.writeRadioSettings(
          this.state.loaded.config.radio,
          updateProgress(channelPlan.blocks.length),
          true
        );
      }

      this.setState({
        statusTone: "good",
        statusMessage: "Write complete. Requesting receiver restart...",
        progressCurrent: totalBlocks,
        progressTotal: totalBlocks,
        progressTone: "success"
      });

      this.awaitingPostWriteDisconnect = true;
      await this.radio.restartRadio();
      await this.radio.disconnect();
      this.awaitingPostWriteDisconnect = false;

      this.setState({
        busy: false,
        connection: null,
        statusTone: "good",
        progressTone: "success",
        statusMessage: "Write Complete. Please wait for receiver to finish rebooting."
      });
      this.postStatus("write-succeeded", "Configuration written successfully.");
    } catch (error) {
      const message = "There was a minor hiccup talking to the receiver. Click Write again to finish the job.";
      const writeSupportDetail = formatWriteSupportDetail(
        error,
        this.state.progressCurrent,
        this.state.progressTotal
      );
      this.awaitingPostWriteDisconnect = false;
      await this.radio.disconnect().catch(() => {});
      this.setState({
        busy: false,
        connection: null,
        statusTone: "err",
        statusMessage: message,
        progressTone: "failure",
        lastError: "",
        writeSupportDetail
      });
      this.postStatus("write-failed", message);
    }
  }
}

const root = document.getElementById("app");
if (!root) {
  throw new Error("Missing #app root element");
}

new SpotWebWriterApp(root).mount();
